import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createApiClient } from '@zaogig/api-client';
import type {
  PhoneLoginParams,
  PhoneLoginResponse,
  WeChatBindParams,
  WeChatLoginResponse,
} from '@zaogig/types';
import { AuthContext, type AuthContextValue, type AuthUser } from './AuthContext';

// ─── Token helpers (write BOTH old + new keys) ─────
const NEW_KEYS = {
  token: 'zaogig_token',
  refreshToken: 'zaogig_refresh_token',
  user: 'zaogig_user',
} as const;

const OLD_KEYS = {
  accessToken: 'accessToken',
  authToken: 'auth_token',
  legacyToken: 'token',
  refreshToken: 'refreshToken',
  account: 'zaogig_account',
};

const SESSION_UPDATED_EVENT = 'zaogig:auth-session-updated';

function getStoredToken(): string | null {
  return localStorage.getItem(NEW_KEYS.token) || localStorage.getItem(OLD_KEYS.legacyToken) || null;
}
function getStoredRefreshToken(): string | null {
  return localStorage.getItem(NEW_KEYS.refreshToken) || localStorage.getItem(OLD_KEYS.refreshToken) || null;
}
function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(NEW_KEYS.user);
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}
function clearStoredSession(): void {
  [...Object.values(NEW_KEYS), ...Object.values(OLD_KEYS)].forEach(k => localStorage.removeItem(k));
  sessionStorage.clear();
}
function storeSession(token: string, refreshToken: string, user: AuthUser, fullResponse?: PhoneLoginResponse): void {
  localStorage.setItem(NEW_KEYS.token, token);
  localStorage.setItem(NEW_KEYS.refreshToken, refreshToken);
  localStorage.setItem(NEW_KEYS.user, JSON.stringify(user));
  localStorage.setItem(OLD_KEYS.accessToken, token);
  localStorage.setItem(OLD_KEYS.authToken, token);
  localStorage.setItem(OLD_KEYS.legacyToken, token);
  localStorage.setItem(OLD_KEYS.refreshToken, refreshToken);
  if (fullResponse) {
    localStorage.setItem(OLD_KEYS.account, JSON.stringify(fullResponse));
  } else {
    localStorage.setItem(OLD_KEYS.account, JSON.stringify(user));
  }
}

function toAuthUser(response: PhoneLoginResponse): AuthUser {
  return {
    accountId: response.account_id,
    currentRole: response.current_role as 'worker' | 'employer',
    roles: response.roles,
    nextStep: response.next_step,
  };
}


function persistAuthenticatedResponse(response: PhoneLoginResponse): AuthUser {
  const user = toAuthUser(response);
  storeSession(response.token, response.refresh_token, user, response);
  window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT, { detail: user }));
  return user;
}

const DEFAULT_BASE_URL = 'http://localhost:3000/api';

const apiClient = createApiClient({
  baseUrl: DEFAULT_BASE_URL,
  getToken: getStoredToken,
  getRefreshToken: getStoredRefreshToken,
  onTokenRefreshed(token, refreshToken) {
    localStorage.setItem(NEW_KEYS.token, token);
    localStorage.setItem(NEW_KEYS.refreshToken, refreshToken);
    localStorage.setItem(OLD_KEYS.accessToken, token);
    localStorage.setItem(OLD_KEYS.authToken, token);
    localStorage.setItem(OLD_KEYS.legacyToken, token);
    localStorage.setItem(OLD_KEYS.refreshToken, refreshToken);
  },
  onAuthFailure() {
    clearStoredSession();
  },
});

export { apiClient };

export async function wechatLogin(code: string): Promise<WeChatLoginResponse> {
  const response = await apiClient.post<WeChatLoginResponse>('/v1/auth/wechat-login', { code });
  if (response.next_step !== 'bind_wechat') {
    persistAuthenticatedResponse(response);
  }
  return response;
}

export async function wechatBind(params: WeChatBindParams): Promise<WeChatLoginResponse> {
  const response = await apiClient.post<WeChatLoginResponse>('/v1/auth/bind-wechat', {
    phone: params.phone,
    code: params.code,
    wechat_code: params.wechatCode,
    selected_role: params.selectedRole,
  });
  persistAuthenticatedResponse(response);
  return response;
}

interface AuthProviderProps {
  children: ReactNode;
  baseUrl?: string;
}

export function AuthProvider({ children, baseUrl }: AuthProviderProps) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    if (baseUrl) {
      apiClient.setBaseUrl(baseUrl);
    }
    const stored = getStoredUser();
    if (stored) setUser(stored);
    setLoading(false);

    function handleSessionUpdated(event: Event) {
      setUser((event as CustomEvent<AuthUser>).detail);
    }
    window.addEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
    return () => window.removeEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
  }, [baseUrl]);

  const login = useCallback(async (params: PhoneLoginParams): Promise<PhoneLoginResponse> => {
    const res = await apiClient.post<PhoneLoginResponse>('/v1/auth/phone-login', {
      login_type: 'phone',
      phone: params.phone,
      code: params.code,
      selected_role: params.selectedRole,
    });
    const authUser = toAuthUser(res);
    storeSession(res.token, res.refresh_token, authUser);
    setUser(authUser);
    return res;
  }, []);

  const logout = useCallback(() => {
    clearStoredSession();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, token: getStoredToken(), refreshToken: getStoredRefreshToken() }),
    [user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
